/**
 * archiveSearchContainer.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2023 Secured Communications. All rights reserved.
 *
**/

import { DateTime } from "luxon";
import * as React from "react";

import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DateTimeValidationError } from "@mui/x-date-pickers/models";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Autocomplete, Button, Checkbox, CircularProgress, 
  FormControlLabel, IconButton, TextField, Tooltip
 } from "@mui/material";

import { AuthContext } from "../auth/auth.context";

import { ErrorSnackBar } from "../components/errorSnackBar.component";
import { FileDownloader } from "../components/fileDownloader.component";

import { ArchiveEntry, ContentType, MessageType } from "../models/message";

import { archiveServiceFactory, SearchTerms } from "../services/archive.service";
import { authServiceFactory } from "../services/auth.service";
import { sanitize } from "../services/purify.service";

type SearchKey = "groupID" | "userID" | "devID" | "key";
interface SearchOption {
  key: SearchKey,
  value: string
}

export function ArchiveSearchContainer(): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const [startDate, setStartDate] = React.useState<DateTime>(DateTime.now().startOf("day").minus({months: 6}));
  const [endDate, setEndDate] = React.useState<DateTime>(DateTime.now().endOf("day"));
  const [startDateErr, setStartDateErr] = React.useState<string>("");
  const [endDateErr, setEndDateErr] = React.useState<string>("");
  const [searchOptions, setSearchOptions] = React.useState<Array<SearchOption>>([]);
  const [selectedTerms, setSelectedTerms] = React.useState<Array<SearchOption>>([]);
  const [messages, setMessages] = React.useState<Array<ArchiveEntry>>([]);
  const [renderContent, setRenderContent] = React.useState<boolean>(false);
  const [showImages, setShowImages] = React.useState<boolean>(false);
  const [dlLink, setDlLink] = React.useState<string>("");
  const [searching, setSearching] = React.useState<boolean>(false);
  const [downloading, setDownloading] = React.useState<boolean>(false);
  const [errMsg, setErrMsg] = React.useState<string>("");

  function updateRendering(event: React.ChangeEvent<HTMLInputElement>) {
    setRenderContent(event.target.checked);
  }
  function updateShowImages(event: React.ChangeEvent<HTMLInputElement>) {
    setShowImages(event.target.checked);
  }

  function onBlur(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    if (propName === "startDate") {
      if (!startDate || startDate.invalidReason) {
        setStartDateErr("Please, set a starting date/time");
      } else if (endDate && endDate < startDate) {
        setStartDateErr("Starting date/time is after the ending date/time");
      } else {
        setStartDateErr("");
      }
    } else if (propName === "endDate") {
      if (!endDate || endDate.invalidReason) {
        setEndDateErr("Please, set an ending date/time");
      } else if (startDate && endDate < startDate) {
        setEndDateErr("Ending date/time is before the starting date/time");
      } else {
        setEndDateErr("");
      }
    }
  }
  function onDateError(error: DateTimeValidationError | null, propName: string) {
    if (propName === "startDate") {
      switch (error) {
        case "maxDate":
          setStartDateErr("Starting date/time must be in the past");
          break;
        case null:
          setStartDateErr("");
          break;
        default:
          setStartDateErr("Please, set a starting date/time");
          break;
      }
    } else if (propName === "endDate") {
      switch (error) {
        case "maxDate":
          setEndDateErr("Ending date/time must be in the past");
          break;
        case null:
          setEndDateErr("");
          break;
        default:
          setEndDateErr("Please, set an ending date/time");
          break;
      }
    }
  }

  function updateSearchTerms(_event: React.SyntheticEvent, newInputValue: string) {
    const options: Array<SearchOption> = [{key: "key", value:  newInputValue}];
    if (/^[\da-fA-F]{1,24}$/.test(newInputValue)) {
      options.push({key: "userID", value: newInputValue});
      options.push({key: "groupID", value: newInputValue});
      options.push({key: "devID", value: newInputValue});
    }
    setSearchOptions(options);
  }
  function nameForKey(key: SearchKey): string {
    switch (key) {
      case "devID": return "Device ID:";
      case "userID": return "User ID:";
      case "groupID": return "Group ID:";
      case "key": return "Keyword:";
    }
  }

  function makeOptionLabel(term: SearchOption | string): string {
    if (!term) { return ""; }
    if (typeof term === "string") {
      return `${nameForKey("key")} ${term}`;
    }
    return `${nameForKey(term.key)} ${term.value}`;
  }

  function selectedSearchTerm(_event: React.SyntheticEvent, terms: ReadonlyArray<SearchOption | string>) {
    console.log("selected:", terms);
    setSelectedTerms(terms.filter(t => t && !(typeof t === "string")) as Array<SearchOption>);
  }

  function makeSearchTerms(): SearchTerms {
    const terms: SearchTerms = {};
    for (const term of selectedTerms) {
      switch (term.key) {
        case "groupID":
          if (!terms.groupIDs) { terms.groupIDs = []; }
          terms.groupIDs.push(term.value);
          break;
        case "userID":
          if (!terms.userIDs) { terms.userIDs = []; }
          terms.userIDs.push(term.value);
          break;
        case "devID":
          if (!terms.deviceIDs) { terms.deviceIDs = []; }
          terms.deviceIDs.push(term.value);
          break;
        case "key":
          if (!terms.terms) { terms.terms = []; }
          terms.terms.push(term.value);
          break;
      }
    }
    return terms;
  }

  async function search() {
    if (searching) { return; }
    if (!authContext.membership) { return; }
    try {
      setSearching(true);
      const terms: SearchTerms = makeSearchTerms();
      const results = await archiveServiceFactory().searchArchive(
        authContext.membership.accountID, startDate, endDate, terms);
      setMessages(results);
    } catch (err) {
      console.error("Error searching:", err);
      setErrMsg((err as Error).message);
    } finally {
      setSearching(false);
    }
  }

  async function download() {
    if (downloading) { return; }
    if (!authContext.membership) { return; }
    try {
      setDownloading(true);
      const terms: SearchTerms = makeSearchTerms();
      const token = authServiceFactory().accessToken;
      const link = archiveServiceFactory().exportLink(authContext.membership.accountID,
        token, startDate, endDate, terms);
      setDlLink(link);
    } catch (err) {
      console.error("Error downloading msgs:", err);
      setErrMsg((err as Error).message);
    } finally {
      setDownloading(false);
    }
  }

  /* 
  renderInput={(props: TextFieldProps) =>
              <TextField
                size="small"
                name="startDate"
                color="secondary"
                type="datetime-local"
                error={!!startDateErr}
                helperText={startDateErr}
                onBlur={onBlur}
                {...props}
              />}
    inputFormat="LL/dd/yyyy h:mm a"
    renderInput={(props: TextFieldProps) =>
              <TextField
                size="small"
                name="endDate"
                color="secondary"
                type="datetime-local"
                error={!!endDateErr}
                helperText={endDateErr}
                onBlur={onBlur}
                {...props}
              />}
  */
  return <div className="archive-search-container">
    <h2>Search Archive</h2>
    <form className="form-wrapper">
      <div className="left-justified-row">
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <DateTimePicker className="start-date-input"
            label="Start Date/Time"
            name="startDate"
            disableFuture
            value={startDate}
            onChange={(newValue: DateTime | null) => {
              newValue && setStartDate(newValue);
            }}
            onError={e => onDateError(e, "startDate")}
            slotProps={{
              textField: {
                size: "small",
                helperText: startDateErr,
                error: !!startDateErr,
                onBlur: onBlur
              }
            }}
          />
          <DateTimePicker className="end-date-input"
            label="End Date/Time"
            name="endDate"
            value={endDate}
            onChange={(newValue: DateTime | null) => {
              newValue && setEndDate(newValue);
            }}
            onError={e => onDateError(e, "endDate")}
            slotProps={{
              textField: {
                size: "small",
                helperText: endDateErr,
                error: !!endDateErr,
                onBlur: onBlur
              }
            }}
          />
        </LocalizationProvider>
        <Autocomplete fullWidth
          multiple
          freeSolo
          size="small"
          options={searchOptions}
          getOptionLabel={makeOptionLabel}
          onChange={selectedSearchTerm}
          onInputChange={updateSearchTerms}
          renderInput={(params) =>
            <TextField variant="outlined" color="secondary" {...params} placeholder="Search:" />} />
        <Button variant="contained" onClick={search} disabled={searching}>
          {searching ? <CircularProgress size={21} /> : "Search"}
        </Button>
        <IconButton size="small" aria-label="download" onClick={download} disabled={searching}>
          <Tooltip title="Download">
            <i className="fa-solid fa-download" />
          </Tooltip>
        </IconButton>
        <FileDownloader src={dlLink} didTrigger={() => setDlLink("") } />
        <ErrorSnackBar errMsg={errMsg} didClose={() => setErrMsg("") } />
      </div>
      <div className="left-justified-row">
        <FormControlLabel className="toggle-rendering"
              control={<Checkbox checked={renderContent} onChange={updateRendering} />}
              label="Render Content" />
        {renderContent && <FormControlLabel className="toggle-images"
              control={<Checkbox checked={showImages} onChange={updateShowImages} />}
              label="Show Images" />}
      </div>
    </form>
    <table className="message-search-table">
      <tbody>
        <tr>
          <th className="time-col">Time</th>
          <th className="groupID-col">Group ID</th>
          <th className="threadID-col">Thread ID</th>
          <th className="id-col">User ID</th>
          <th className="deviceID-col">Device ID</th>
          <th className="evt-col">Msg/Event</th>
        </tr>
        {messages.map(entry => <tr key={entry._id}>
          <td className="time-col">
            <Tooltip title={<article>
              <div>G: {entry.groupname || ""}</div>
              <div>U: {entry.firstname || ""} {entry.lastname || ""}</div>
              <div>D: {entry.deviceName || ""}</div>
            </article>} >
              <i className="fa-solid fa-circle-info" />
            </Tooltip>
            {entry.sent.toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)}
          </td>
          <td className="groupID-col">{entry.groupID}</td>
          <td className="threadID-col">{entry.threadID || ""}</td>
          <td className="id-col">{entry.userID || ""}</td>
          <td className="deviceID-col">{entry.deviceID || ""}</td>
          <ArchiveEntryEventCell entry={entry}
            renderContent={renderContent}
            showImages={showImages} />
        </tr>
      )}
      </tbody>
    </table>

  </div>;
}

interface AEProps {
  entry: ArchiveEntry,
  renderContent: boolean,
  showImages: boolean
}
function ArchiveEntryEventCell(props: AEProps): React.ReactElement<any, any> {
  const entry = props.entry;

  switch (entry.msgtype) {
    case MessageType.CONTENT:
    case MessageType.DEV_ADDED:
    case MessageType.CTRL:
    case MessageType.USER_JOINED:
    case MessageType.DEV_JOINED:
    case MessageType.DEV_REM:
      return <ArchiveEntryContentComponent {...props} />;
    case MessageType.REMOVE_MSG:
      return <td className="evt-col"><span className="noncontent">removed message w/ id: {entry.msgID}</span></td>;
    case MessageType.READ:
      return <td className="evt-col"><span className="noncontent">read receipt</span></td>;
  }
  return <></>;
}

function ArchiveEntryContentComponent(props: AEProps): React.ReactElement<any, any> {
  const classes = props.entry.msgtype === MessageType.CONTENT ? "evt-col content" : "evt-col noncontent";
  if (props.renderContent && props.entry.contenttype === ContentType.html) {
    if (!props.entry.sanitizedContent) {
      props.entry.sanitizedContent = sanitize(props.entry.content || "");
    }
    const filtered = props.showImages ?
      props.entry.sanitizedContent :
      props.entry.sanitizedContent.replace(/<img .*?>/g, "<i>&lt;img redacted&gt;</i>");
    return <td className={classes} dangerouslySetInnerHTML={{__html: filtered}}></td>;
  }
  const filtered = (props.entry.content || "").replace(/<img .*?>/g, "<i>&lt;img redacted&gt;</i>");
  return <td className={classes}>{filtered}</td>;
}
